import { MemberDistributionResponse, SelectionModeType } from "_clients/BFFClient";
import { ToggleWithState } from "_components/atoms";
import ProgressBar from "_components/atoms/ProgressBar/ProgressBar";
import { TinyLoader } from "_components/atoms/TinyLoader/TinyLoader";
import { BoldSpan, BoldText, BottomRegularText, CreditsConsumedContainer, DisabledRowContainer, GrayTextContainer, IconTooltipContainer, IncrementCounterContainer, InfoIcon, MemberInformationsContainer, ModeAutoContainer, ReadOnlyIncrementCounter, RegularText, StyledIncrementCounter, WhiteTooltip } from "_components/molecules/CreditDispatchTable/CreditDispatchTable.styled";
import { DistributionUpdate, AutoModeUpdate } from "_components/molecules/CreditDispatchTable/CreditDispatchTable.types";
import { DashboardParamsType } from "_components/organisms/CreditDispatchDashboard/CreditDispatchDashboard.types";
import { getProductTypeEnumFromId } from "_components/organisms/Dashboard/Dashboard.types";
import { formatDateToFrench } from "_shared/helpers/dateHelper";
import { useAvailableBoostStore } from "_stores/availableBoost/availableBoost";
import React from "react";
import { useEffect, useState } from "react";



export const useColumns = (networkMarkets, isDataLoading: boolean, updateRepartitionStarted: boolean, updateModeAutoStarted: boolean, isEligibleAutoSelection: boolean,
    searchParams: DashboardParamsType, distribUpdate: DistributionUpdate, automodeUpdate: AutoModeUpdate, handleOnChange, handleAutoModeOnChange, dependencies) => {

    const { networkId, contractId, marketId, productType } = searchParams;
    const { findAvailableBoost } = useAvailableBoostStore()

    useEffect(() => {
        setColumns(renderColumns());
    }, dependencies);

    const renderMemberCell = (props) => {
        const { name, isEnabled, rcuID, zipCode, city, addDate, commercialName } = props.row.original;
        return (
            <MemberInformationsContainer isLoading={isDataLoading} disabled={!isEnabled}>
                <div>
                    <BoldText> {name} </BoldText>
                    <BoldText> {commercialName} </BoldText>
                    {rcuID && (<RegularText>ID RCU : {rcuID} </RegularText>)}
                </div>
                <GrayTextContainer disabled={!isEnabled}>
                    <RegularText>{`${city} (${zipCode})`}</RegularText>
                    {addDate && (
                        <BottomRegularText>
                            {" "}
                            {`Date d'activation : ${formatDateToFrench(addDate)}`}
                        </BottomRegularText>
                    )}
                </GrayTextContainer>
            </MemberInformationsContainer>
        );
    };

    const renderConsumedCell = (props) => {
        const { row } = props;
        const allocatedBoosts = row.original.allocatedBoosts ?? 0;
        const consumedBoosts = row.original.consumedBoosts ?? 0;
        const progressPercentage = (consumedBoosts * 100) / allocatedBoosts;
        return (
            <CreditsConsumedContainer isLoading={isDataLoading} disabled={!row.original.isEnabled} data-testid={`consumed-cell-${row.original.rcuID}`}>
                <ProgressBar progress={progressPercentage} disabled={!row.original.isEnabled} />
                <RegularText>
                    <BoldSpan>{consumedBoosts} </BoldSpan>/ {allocatedBoosts}{" "}
                    {allocatedBoosts > 1 ? " boosts utilisés" : "boost utilisé"}
                </RegularText>
            </CreditsConsumedContainer>
        );
    };

    const incrementCounterErrorHandler = (value, min, max) => {
        if (value > max) {
            return `Le nombre de boosts ne peut dépasser ${max}.`
        }
        if (value < min) {
            return `Le nombre de boosts ne peut être inferieur à  ${min}.`
        }
    }

    const useRenderRepartitionCell = (props) => {
        const { allocatedBoosts, consumedBoosts, isEnabled, rcuID } = props.row.original;
        let total;
        const availableBoost = findAvailableBoost(networkId, contractId, Number(marketId), productType);
        if (availableBoost) {
            total = availableBoost.newTotalAvailableBoosts;
        } else {
            const market = (networkMarkets ?? []).find(m => m.id == marketId);
            const product = market?.products?.find(p => productType == getProductTypeEnumFromId(p.id.toString()));
            if (product) {
                total = networkId ? product.totalAvailableBoosts : (product.contracts.find(c => c.contractId == contractId)?.available || 0);
            }
        }
        const isPerformingUpdate = updateRepartitionStarted && Object.values(distribUpdate).some(d => d.rcuId == rcuID &&
            d.networkId == networkId && d.marketId == marketId && d.productType == productType)
        const [showTooltip, setShowTooltip] = useState(false);
        const buttonTooltipContainerRef = React.createRef<HTMLDivElement>();

        return isEnabled ?
            <div>
                {isPerformingUpdate ?
                    <TinyLoader marginLeft={35} />
                    :
                    <IncrementCounterContainer isLoading={isDataLoading}>
                        {total == undefined ?
                            <ReadOnlyIncrementCounter initialValue={allocatedBoosts} /> :
                            <StyledIncrementCounter
                                initialValue={allocatedBoosts}
                                min={consumedBoosts}
                                max={total + allocatedBoosts}
                                onChange={value => handleOnChange(value, rcuID, allocatedBoosts)}
                                testID={`increment-counter-${rcuID}`}
                                errorMessage={incrementCounterErrorHandler}
                            />
                        }
                    </IncrementCounterContainer>
                }
            </div>
            :
            !isDataLoading &&
            <DisabledRowContainer data-testid={`disabled-cell-repartition-${rcuID}`}>
                <div>
                    <IncrementCounterContainer isLoading={isDataLoading}>
                        <ReadOnlyIncrementCounter min={0} max={0} initialValue={0} />
                    </IncrementCounterContainer>
                </div>
                <IconTooltipContainer onMouseOver={() => setShowTooltip(true)} onMouseOut={() => setShowTooltip(false)} ref={buttonTooltipContainerRef}>
                    <InfoIcon />
                    {showTooltip &&
                        <WhiteTooltip anchorRef={buttonTooltipContainerRef} orientation="right">
                            <p>Vous pourrez bientôt attribuer des crédits à ce membre.</p>
                            <p>Si l&apos;activation prend trop de temps nous vous conseillons de vous rapprocher de notre service support.</p>
                        </WhiteTooltip>}
                </IconTooltipContainer>
            </DisabledRowContainer>
    }

    const renderAutomaticModeCell = (props) => {
        const { selectionModeType, isEnabled, rcuID } = props.row.original;
        const inFlyValue = automodeUpdate===undefined? [] : Object.values(automodeUpdate)
                                                                    .filter(d => d.rcuId == rcuID && d.networkId == networkId
                                                                        && d.marketId == marketId && d.productType == productType)

        const isPerformingUpdate = updateModeAutoStarted && inFlyValue.length > 0

        const autoModeActif =  inFlyValue.length? inFlyValue[0].selectionModeType == SelectionModeType.Auto : selectionModeType == SelectionModeType.Auto;

        if (!isEnabled) return <div data-testid={`notoggle-automode-${rcuID}`}></ div>

        if (isPerformingUpdate) return <TinyLoader marginLeft={35} />

        return (
            <ModeAutoContainer isLoading={isDataLoading}>
                <ToggleWithState
                    checked={autoModeActif}
                    onChange={value => handleAutoModeOnChange(value, rcuID)}
                    testID={`toggle-automode-${rcuID}`}
                />
            </ModeAutoContainer>
        )
    }

    const renderColumns = () => {
        let columns = [
            {
                header: "Membre",
                accessor: "name",
                className: "size-1",
                disableSortBy: true,
                Cell: renderMemberCell,
            },
            {
                header: "Consommés",
                id: "2",
                accessor: (data: MemberDistributionResponse) =>
                    (data.consumedBoosts * 100) / data.allocatedBoosts,
                disableSortBy: true,
                className: "size-1",
                Cell: renderConsumedCell,
            },
            {
                header: "Répartir des boosts",
                id: "3",
                className: "size-1",
                disableSortBy: true,
                Cell: useRenderRepartitionCell
            }];

        if (isEligibleAutoSelection) {
            columns = [...columns,
            {
                header: "Affectation automatique",
                id: "5",
                disableSortBy: true,
                className: "size-1",
                Cell: renderAutomaticModeCell
            }
            ];
        }
        return columns;
    }

    const [columns, setColumns] = useState(renderColumns());

    return columns;
};


